import { useState } from 'preact/hooks';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { route } from 'preact-router';

import { T, appThemes, applyTheme, getStoredTheme, card } from '@/ui/tokens';
import { Icons, sized } from '@/ui/icons';
import { BrandMail, BrandTelegram } from '@/ui/icons-brand';
import { TopBar } from '@/ui/primitives/TopBar';
import { Spinner } from '@/ui/primitives/Spinner';
import { homeRepo } from '@/data/repositories/home-repo';
import { authRepo } from '@/data/repositories/auth-repo';
import { tokenStore } from '@/data/storage/token-store';
import { fmt } from '@/ui/utils/format';
import { config } from '@/config';
import type { ComponentChildren } from 'preact';

// Keep in sync with AuthService.MaxCoinsForDeletion on the backend (the server
// re-checks and is the source of truth).
const MAX_COINS_FOR_DELETE = 100;

export function SettingsScreen() {
  const qc = useQueryClient();
  const home = useQuery({ queryKey: ['home'], queryFn: homeRepo.home });
  const contact = useQuery({ queryKey: ['contact'], queryFn: homeRepo.contact, staleTime: 5 * 60_000 });

  const [confirming, setConfirming] = useState(false);
  const [theme, setTheme] = useState(getStoredTheme);

  const coins = home.data?.coins ?? 0;
  const active = home.data?.activeorders ?? 0;
  const balanceOk = coins < MAX_COINS_FOR_DELETE;
  const noActiveOrders = active === 0;
  const canDelete = !home.isPending && balanceOk && noActiveOrders;

  const del = useMutation({
    mutationFn: authRepo.deleteAccount,
    onSuccess: () => {
      tokenStore.clear();
      qc.clear();
      route(config.routes.login);
    },
  });

  const email = contact.data?.email ?? '';
  const tgSupport = normalizeTelegram(contact.data?.telegram);
  const tgChannel = normalizeTelegram(contact.data?.telegramChannel);

  const selectTheme = (nextTheme: typeof theme) => {
    setTheme(nextTheme);
    applyTheme(nextTheme);
  };

  return (
    <div style={{ background: T.bg, minHeight: '100%' }}>
      <TopBar title="Settings" onBack={() => history.back()}/>

      <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 16 }}>

        {/* ---- Appearance ---- */}
        <div style={{ ...card, padding: 18 }}>
          <div style={{ fontSize: 11, color: T.textMute, textTransform: 'uppercase',
            letterSpacing: 1, fontWeight: 500, marginBottom: 14 }}>Theme</div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(96px, 1fr))', gap: 10 }}>
            {appThemes.map((option) => {
              const selected = option.id === theme;
              return (
                <button key={option.id} type="button" onClick={() => selectTheme(option.id)}
                  aria-pressed={selected}
                  style={{
                    minWidth: 0,
                    minHeight: 78,
                    padding: '11px 8px',
                    borderRadius: 12,
                    border: `1px solid ${selected ? T.accent : T.line}`,
                    background: selected ? 'color-mix(in srgb, var(--c-accent) 12%, transparent)' : T.surface2,
                    color: T.text,
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 9,
                  }}>
                  <span style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                    {option.swatches.map((swatch) => (
                      <span key={swatch} style={{
                        width: 16,
                        height: 16,
                        borderRadius: '50%',
                        background: swatch,
                        border: `1px solid ${T.line2}`,
                      }}/>
                    ))}
                  </span>
                  <span style={{ fontSize: 13, fontWeight: 700, overflow: 'hidden',
                    textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%' }}>
                    {option.label}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* ---- Contact / support ---- */}
        <div style={{ ...card, padding: 18 }}>
          <div style={{ fontSize: 11, color: T.textMute, textTransform: 'uppercase',
            letterSpacing: 1, fontWeight: 500, marginBottom: 14 }}>Contact us</div>

          {contact.isPending ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 12 }}><Spinner/></div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {email && (
                <ContactRow href={`mailto:${email}`} icon={<BrandMail size={20} color={T.text}/>}
                  label="Email" value={email}/>
              )}
              {tgSupport && (
                <ContactRow href={tgSupport.href} icon={<BrandTelegram size={20}/>}
                  label="Telegram support" value={tgSupport.value}/>
              )}
              {tgChannel && (
                <ContactRow href={tgChannel.href} icon={<BrandTelegram size={20}/>}
                  label="Telegram channel" value={tgChannel.value}/>
              )}
              {!email && !tgSupport && !tgChannel && (
                <div style={{ fontSize: 13, color: T.textDim }}>No contact details available.</div>
              )}
            </div>
          )}
        </div>

        {/* ---- Delete account ---- */}
        <div style={{ ...card, padding: 18, borderColor: 'rgba(255,90,110,0.25)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
            <span style={{ color: T.danger }}>{sized(Icons.logout, 18)}</span>
            <div style={{ fontSize: 14, fontWeight: 700, color: T.text }}>Delete account</div>
          </div>
          <p style={{ margin: '0 0 14px', fontSize: 13, color: T.textDim, lineHeight: 1.55 }}>
            Permanently removes your account and all related data (orders, deposits,
            withdrawals, referrals). This can't be undone. Allowed only when:
          </p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 16 }}>
            <Condition met={balanceOk}
              label={`Balance under ${MAX_COINS_FOR_DELETE} coins`}
              detail={`You have ${fmt(coins)} C`}/>
            <Condition met={noActiveOrders}
              label="No active orders"
              detail={active > 0 ? `${active} still active` : 'None active'}/>
          </div>

          {del.isError && (
            <div style={{ marginBottom: 12, fontSize: 13, color: T.danger }}>
              {(del.error as Error).message}
            </div>
          )}

          {!confirming ? (
            <button onClick={() => setConfirming(true)} disabled={!canDelete} style={{
              width: '100%', padding: '13px', borderRadius: 12, border: 'none',
              background: canDelete ? T.danger : T.surface2,
              color: canDelete ? '#fff' : T.textMute,
              fontFamily: T.font, fontSize: 14, fontWeight: 700,
              cursor: canDelete ? 'pointer' : 'not-allowed',
            }}>
              {canDelete ? 'Delete my account' : 'Conditions not met'}
            </button>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div style={{ fontSize: 13, color: T.text, fontWeight: 600 }}>
                Are you sure? This is permanent.
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <button onClick={() => setConfirming(false)} disabled={del.isPending} style={{
                  flex: 1, padding: '13px', borderRadius: 12, background: 'transparent',
                  color: T.text, border: `1px solid ${T.line2}`,
                  fontFamily: T.font, fontSize: 14, fontWeight: 600, cursor: 'pointer',
                }}>Cancel</button>
                <button onClick={() => del.mutate()} disabled={del.isPending} style={{
                  flex: 1, padding: '13px', borderRadius: 12, border: 'none',
                  background: T.danger, color: '#fff',
                  fontFamily: T.font, fontSize: 14, fontWeight: 700,
                  cursor: del.isPending ? 'wait' : 'pointer',
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                }}>
                  {del.isPending && <Spinner size={14}/>}
                  Yes, delete
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function normalizeTelegram(value?: string | null): { href: string; value: string } | null {
  const raw = (value ?? '').trim();
  if (!raw) return null;

  const fromUrl = raw.match(/^https?:\/\/t\.me\/([^/?#]+)/i);
  const handle = (fromUrl?.[1] ?? raw).replace(/^@/, '').trim();
  if (!handle) return null;

  return { href: `https://t.me/${handle}`, value: `@${handle}` };
}

function ContactRow({ href, icon, label, value }: {
  href: string; icon: ComponentChildren; label: string; value: string;
}) {
  const [copied, setCopied] = useState(false);
  const isEmail = href.startsWith('mailto:');

  const onClick = (e: Event) => {
    if (isEmail) {
      e.preventDefault();
      navigator.clipboard.writeText(value).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      });
    }
  };

  return (
    <a href={href} onClick={onClick} target="_blank" rel="noreferrer" style={{
      display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px',
      borderRadius: 12, background: T.surface2, border: `1px solid ${T.line}`,
      textDecoration: 'none',
    }}>
      <span style={{ flexShrink: 0, display: 'inline-flex' }}>{icon}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 10, color: T.textMute, textTransform: 'uppercase',
          letterSpacing: 1, fontWeight: 500 }}>{label}</div>
        <div style={{ fontSize: 14, color: T.text, fontWeight: 500,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{value}</div>
      </div>
      <span style={{
        fontSize: 12, color: T.accent, fontWeight: 600,
        display: isEmail && copied ? 'inline' : 'none',
      }}>Copied!</span>
      {!copied && <span style={{ color: T.textMute }}>{sized(Icons.arrR, 16)}</span>}
    </a>
  );
}

function Condition({ met, label, detail }: { met: boolean; label: string; detail: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <span style={{
        width: 20, height: 20, borderRadius: '50%', flexShrink: 0,
        background: met ? 'rgba(57,229,140,0.15)' : 'rgba(255,90,110,0.15)',
        color: met ? T.ok : T.danger,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      }}>{sized(met ? Icons.check : Icons.x, 13, 2.5)}</span>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 13, color: T.text, fontWeight: 500 }}>{label}</div>
      </div>
      <div style={{ fontSize: 12, color: met ? T.textDim : T.danger,
        fontFamily: T.fontMono }}>{detail}</div>
    </div>
  );
}
